/**
 * Service-account admin client (ADR-0052) — `/api/admin/service-accounts`.
 * A service account is a provisioned integration IDENTITY (not a human) with its
 * OWN permissions + app scope; its keys are rotatable secrets pointing at it.
 */
import { api } from "@/lib/api";

/** ADR-0053 — the explicit TARGET of a service account. */
export type ServiceAccountScope = "tray" | "app";

export interface ServiceAccount {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  scope: ServiceAccountScope;
  app_id: string | null;
  /** ADR-0053 S6: an app SA acts AS this app role (its /svc boundary). NULL for tray. */
  role_id: string | null;
  app_member_attrs: Record<string, unknown> | null;
  enabled: boolean;
  created_by_type: string;
  created_by_id: string;
  created_at: string;
}

export interface ServiceAccountKey {
  key_id: string;
  name: string | null;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface ServiceAccountKeyCreated extends ServiceAccountKey {
  value: string; // plaintext, shown ONCE
}

export interface ServiceAccountInput {
  name?: string;
  description?: string | null;
  permissions?: string[];
  scope?: ServiceAccountScope;
  app_id?: string | null;
  role_id?: string | null;
  app_member_attrs?: Record<string, unknown> | null;
  enabled?: boolean;
}

const base = "/service-accounts";

export const serviceAccountsApi = {
  list: () => api.get<ServiceAccount[]>(base),
  get: (id: string) => api.get<ServiceAccount>(`${base}/${id}`),
  create: (body: ServiceAccountInput) => api.post<ServiceAccount>(base, body),
  update: (id: string, body: ServiceAccountInput) => api.patch<ServiceAccount>(`${base}/${id}`, body),
  remove: (id: string) => api.delete<void>(`${base}/${id}`),

  listKeys: (id: string) => api.get<ServiceAccountKey[]>(`${base}/${id}/keys`),
  mintKey: (id: string, name?: string) =>
    api.post<ServiceAccountKeyCreated>(`${base}/${id}/keys`, { name }),
  revokeKey: (id: string, keyId: string) => api.delete<void>(`${base}/${id}/keys/${keyId}`),
};
